import { useState, useEffect, useCallback } from 'react'
import type { Vulnerability, VulnerabilityStats } from '../types/vulnerability'
import type { WorkerResponse } from '../workers/dataWorker'

interface UseVulnerabilityDataResult {
  data: Vulnerability[]
  stats: VulnerabilityStats | null
  isLoading: boolean
  progress: number
  progressMessage: string
  error: string | null
  reload: () => void
  worker: Worker | null  // Expose worker for filtering
}

// Always load from R2 - local file is 370MB and gitignored
const DATA_URL = 'https://pub-c3a778ccfd1441d48c934349936c7e75.r2.dev/vulnerabilities.json'

export function useVulnerabilityData(): UseVulnerabilityDataResult {
  const [data, setData] = useState<Vulnerability[]>([])
  const [stats, setStats] = useState<VulnerabilityStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [progress, setProgress] = useState(0)
  const [progressMessage, setProgressMessage] = useState('Initializing...')
  const [error, setError] = useState<string | null>(null)
  const [worker, setWorker] = useState<Worker | null>(null)

  const initWorker = useCallback(() => {
    // Create worker using Vite's worker import
    const newWorker = new Worker(
      new URL('../workers/dataWorker.ts', import.meta.url),
      { type: 'module' }
    )

    newWorker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const response = event.data

      switch (response.type) {
        case 'PROGRESS':
          setProgress(response.progress)
          setProgressMessage(response.message)
          break

        case 'PARSE_COMPLETE':
          setData(response.data)
          setStats(response.stats)
          setIsLoading(false)
          setProgress(100)
          setProgressMessage('Complete!')
          break

        case 'ERROR':
          setError(response.error)
          setIsLoading(false)
          break
      }
    }

    newWorker.onerror = (err) => {
      setError(err.message || 'Worker error')
      setIsLoading(false)
    }

    setWorker(newWorker)
    return newWorker
  }, [])

  const loadData = useCallback((workerInstance: Worker) => {
    setIsLoading(true)
    setError(null)
    setProgress(0)
    setProgressMessage('Starting...')

    workerInstance.postMessage({ type: 'PARSE_DATA', url: DATA_URL })
  }, [])

  const reload = useCallback(() => {
    if (worker) {
      loadData(worker)
    }
  }, [worker, loadData])

  useEffect(() => {
    const workerInstance = initWorker()
    loadData(workerInstance)

    return () => {
      workerInstance.terminate()
    }
  }, [initWorker, loadData])

  return {
    data,
    stats,
    isLoading,
    progress,
    progressMessage,
    error,
    reload,
    worker,
  }
}
